import React, { useState, useEffect } from 'react';
import {
  ThemeProvider,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Button,
} from '@mui/material';
import { ArrowDropDown as ArrowDropDownIcon, Menu as MenuIcon } from '@mui/icons-material';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Sidebar, { DRAWER_WIDTH } from './components/Sidebar';
import TodayPage from './pages/TodayPage';
import HistoryPage from './pages/HistoryPage';
import UpcomingPage from './pages/UpcomingPage';
import NotFoundPage from './pages/NotFoundPage';
import MarkdownEditorPage from './pages/MarkdownEditorPage';
import ImageViewerPage from './pages/ImageViewerPage';
import BackpackModal from './components/BackpackModal';
import BackpackIcon from './components/BackpackIcon';
import RAMModal from './components/RAMModal';
import AirmaskInfoModal from './components/AirmaskInfoModal';
import GlobalPasoTagsModal from './components/GlobalPasoTagsModal';
import { RecurringItemsProvider } from './contexts/RecurringItemsContext';
import { theme } from './theme';
import { amplifyAuthService } from './services/amplifyAuth';
import { AIRMASK_GREEN, AIRMASK_DARK } from './constants/colors';

const IDLE_TIMEOUT_MS = 24 * 60 * 60 * 1000;
const IDLE_CHECK_INTERVAL_MS = 60 * 1000;
const LAST_ACTIVITY_AT_KEY = 'airmask:lastActivityAt';

const stripModalSuffix = (path: string): string => {
  return path.replace(/\/(pack|ram|hint)$/, '');
};

function WelcomePage() {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(180deg, #111111 0%, #1A1A1A 100%)',
        px: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 3,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Box
            component="img"
            src="/logo192.png"
            alt="AirMask logo"
            sx={{
              width: { xs: 72, sm: 96 },
              height: { xs: 72, sm: 96 },
              display: 'block',
            }}
          />
          <Typography
            variant="h1"
            sx={{
              color: AIRMASK_GREEN,
              fontWeight: 700,
              letterSpacing: '-0.06em',
              fontSize: { xs: '3rem', sm: '5rem', md: '6rem' },
              lineHeight: 1,
            }}
          >
            AirMask
          </Typography>
        </Box>

        <Button
          variant="contained"
          onClick={() => navigate('/login')}
          sx={{
            backgroundColor: AIRMASK_GREEN,
            color: AIRMASK_DARK,
            fontWeight: 700,
            fontSize: '1.05rem',
            px: 4,
            py: 1.5,
            borderRadius: 999,
            minWidth: 180,
            '&:hover': {
              backgroundColor: '#4ce63f',
            },
          }}
        >
          Sign in
        </Button>
      </Box>
    </Box>
  );
}

function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoginError('');
    setIsSubmitting(true);

    const result = await amplifyAuthService.signInWithPassword(email, password);
    setIsSubmitting(false);

    if (result.success) {
      navigate('/today/log-habits', { replace: true });
      return;
    }

    if (result.code === 'INVALID_CREDENTIALS') {
      setShowForgotPassword(true);
      setLoginError('Invalid email or password.');
      return;
    }

    setLoginError(result.message || 'Unable to sign in.');
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setLoginError('Enter your email first.');
      return;
    }

    const result = await amplifyAuthService.beginForgotPassword(email);
    if (result.success) {
      setForgotPasswordSent(true);
      setLoginError('');
      return;
    }

    setLoginError(result.message || 'Unable to send reset instructions.');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(180deg, #111111 0%, #1A1A1A 100%)',
        px: 2,
      }}
    >
      <Box
        component="form"
        onSubmit={handleLogin}
        sx={{
          width: '100%',
          maxWidth: 420,
          backgroundColor: '#1f1f1f',
          border: `1px solid ${AIRMASK_GREEN}`,
          borderRadius: 3,
          p: 4,
          boxShadow: '0 12px 38px rgba(0, 0, 0, 0.35)',
        }}
      >
        <Typography variant="h4" sx={{ mb: 2, color: AIRMASK_GREEN, fontWeight: 700, textAlign: 'center' }}>
          Put your own mask on first
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1, color: '#d1d1d1' }}>
            Email
          </Typography>
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            style={{
              width: '100%',
              padding: '14px 16px',
              borderRadius: 10,
              border: `1px solid ${AIRMASK_GREEN}`,
              background: '#111111',
              color: '#ffffff',
              fontSize: 16,
              boxSizing: 'border-box',
            }}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1, color: '#d1d1d1' }}>
            Password
          </Typography>
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            style={{
              width: '100%',
              padding: '14px 16px',
              borderRadius: 10,
              border: `1px solid ${AIRMASK_GREEN}`,
              background: '#111111',
              color: '#ffffff',
              fontSize: 16,
              boxSizing: 'border-box',
            }}
          />
        </Box>

        {loginError && (
          <Box sx={{ mb: 2, color: '#ffd6dc', backgroundColor: '#3b1b21', borderRadius: 2, p: 1.5, border: '1px solid #e66b7a' }}>
            {loginError}
          </Box>
        )}

        {showForgotPassword && !forgotPasswordSent && (
          <Box sx={{ mb: 2, textAlign: 'right' }}>
            <button
              type="button"
              onClick={handleForgotPassword}
              style={{
                background: 'transparent',
                border: 'none',
                color: AIRMASK_GREEN,
                cursor: 'pointer',
                fontWeight: 600,
                textDecoration: 'underline',
                fontSize: 14,
              }}
            >
              Forgot your password?
            </button>
          </Box>
        )}

        {forgotPasswordSent && (
          <Box sx={{ mb: 2, color: '#d9f7d4', backgroundColor: '#1f3d1f', borderRadius: 2, p: 1.5, border: '1px solid #6adf6b' }}>
            Password reset instructions were sent to your email.
          </Box>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          style={{
            width: '100%',
            padding: '14px 16px',
            borderRadius: 999,
            border: 'none',
            backgroundColor: AIRMASK_GREEN,
            color: AIRMASK_DARK,
            fontWeight: 700,
            fontSize: 16,
            cursor: isSubmitting ? 'wait' : 'pointer',
            opacity: isSubmitting ? 0.7 : 1,
          }}
        >
          {isSubmitting ? 'Signing in...' : 'Sign in'}
        </button>
      </Box>
    </Box>
  );
}

function AirMaskLoadingScreen() {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(180deg, #111111 0%, #1A1A1A 100%)',
        px: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 3,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Box
            component="img"
            src="/logo192.png"
            alt="AirMask logo"
            sx={{
              width: { xs: 72, sm: 96 },
              height: { xs: 72, sm: 96 },
              display: 'block',
            }}
          />
          <Typography
            variant="h1"
            sx={{
              color: AIRMASK_GREEN,
              fontWeight: 700,
              letterSpacing: '-0.06em',
              fontSize: { xs: '3rem', sm: '5rem', md: '6rem' },
              lineHeight: 1,
            }}
          >
            AirMask
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const [backpackModalOpen, setBackpackModalOpen] = useState(false);
  const [ramModalOpen, setRamModalOpen] = useState(false);
  const [hintModalOpen, setHintModalOpen] = useState(false);
  const [globalTagsModalOpen, setGlobalTagsModalOpen] = useState(false);
  const [isHoveringTopRamZone, setIsHoveringTopRamZone] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [authState, setAuthState] = useState<'loading' | 'authenticated' | 'unauthenticated'>('loading');

  const isPublicLandingRoute = location.pathname === '/' || location.pathname === '/login';
  const isWelcomeRoute = location.pathname === '/';

  useEffect(() => {
    let isMounted = true;
    amplifyAuthService.getSession().then((result) => {
      if (!isMounted) return;
      const nextAuthState = result.authenticated ? 'authenticated' : 'unauthenticated';
      setAuthState(nextAuthState);
      if (!result.authenticated && !isPublicLandingRoute) {
        navigate('/', { replace: true });
      }

      if (result.authenticated && (isWelcomeRoute || location.pathname === '/login')) {
        navigate('/today/log-habits', { replace: true });
      }
    });

    return () => {
      isMounted = false;
    };
  }, [isPublicLandingRoute, isWelcomeRoute, location.pathname, navigate]);

  useEffect(() => {
    if (authState !== 'authenticated') {
      return;
    }

    let isSigningOut = false;

    const writeLastActivity = () => {
      localStorage.setItem(LAST_ACTIVITY_AT_KEY, Date.now().toString());
    };

    const getLastActivity = (): number => {
      const stored = localStorage.getItem(LAST_ACTIVITY_AT_KEY);
      if (!stored) {
        return Date.now();
      }

      const parsed = Number.parseInt(stored, 10);
      if (Number.isNaN(parsed)) {
        return Date.now();
      }

      return parsed;
    };

    const signOutForInactivity = async () => {
      if (isSigningOut) {
        return;
      }
      isSigningOut = true;

      try {
        await amplifyAuthService.signOut();
      } finally {
        localStorage.removeItem(LAST_ACTIVITY_AT_KEY);
        setAuthState('unauthenticated');
        navigate('/', { replace: true });
      }
    };

    const maybeSignOutForInactivity = () => {
      const idleDurationMs = Date.now() - getLastActivity();
      if (idleDurationMs >= IDLE_TIMEOUT_MS) {
        void signOutForInactivity();
      }
    };

    const onActivity = () => {
      writeLastActivity();
    };

    if (!localStorage.getItem(LAST_ACTIVITY_AT_KEY)) {
      writeLastActivity();
    }

    const activityEvents: Array<keyof WindowEventMap> = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, onActivity, { passive: true });
    });

    const onVisibilityChange = () => {
      if (!document.hidden) {
        maybeSignOutForInactivity();
      }
    };

    window.addEventListener('focus', maybeSignOutForInactivity);
    document.addEventListener('visibilitychange', onVisibilityChange);

    const intervalId = window.setInterval(maybeSignOutForInactivity, IDLE_CHECK_INTERVAL_MS);
    maybeSignOutForInactivity();

    return () => {
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, onActivity);
      });
      window.removeEventListener('focus', maybeSignOutForInactivity);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, [authState, navigate]);

  // State for restoring expanded sections in backpack modal
  // These are populated when user navigates back from editor with state
  const [backpackExpandedSections, setBackpackExpandedSections] = useState<string[]>([]);
  const [backpackNestedExpanded, setBackpackNestedExpanded] = useState<string[]>([]);
  const [backpackExpandedFolders, setBackpackExpandedFolders] = useState<string[]>([]);

  const openRamRoute = () => {
    // Hide transient chevron states before route transition to avoid visual jump.
    setIsHoveringTopRamZone(false);

    if (!location.pathname.endsWith('/ram')) {
      const basePath = stripModalSuffix(location.pathname);
      navigate(`${basePath}/ram`);
    }
  };
  
  // Clear sessionStorage on mount (fresh page load/refresh)
  // This prevents backpack modal from opening on page refresh
  // sessionStorage is only meant for active navigation within a session
  useEffect(() => {
    sessionStorage.removeItem('backpackState');
  }, []); // Empty dependency array = runs once on mount
  
  // Effect to detect when user navigates back from editor with backpack state
  // This allows us to restore the modal with the same sections expanded
  useEffect(() => {
    // Check if location.state indicates we should open the backpack
    const state = location.state as any;
    
    if (state?.openBackpack) {
      // Restore which sections should be expanded (from app back button)
      setBackpackExpandedSections(state.expandedSections || []);
      setBackpackNestedExpanded(state.nestedExpanded || []);
      setBackpackExpandedFolders(state.expandedFolders || []);
      
      // Navigate to /pack URL so the address bar reflects the open modal
      // Using replace:true also clears location.state to prevent re-firing
      if (!location.pathname.endsWith('/pack')) {
        navigate(location.pathname + '/pack', { replace: true });
      } else {
        setBackpackModalOpen(true);
      }
    }
    // Check sessionStorage for backpack state (from browser back button)
    else if (
      !location.pathname.startsWith('/editor/') &&
      !location.pathname.startsWith('/viewer/') &&
      !location.pathname.endsWith('/pack') &&
      !location.pathname.endsWith('/ram') &&
      !location.pathname.endsWith('/hint')
    ) {
      const storedState = sessionStorage.getItem('backpackState');
      
      if (storedState) {
        try {
          // Decode the stored backpack state
          const decodedState = JSON.parse(atob(storedState));
          
          if (decodedState.from === 'backpack') {
            // Restore the backpack modal with the stored state
            setBackpackExpandedSections(decodedState.expanded || []);
            setBackpackNestedExpanded(decodedState.nestedExpanded || []);
            setBackpackExpandedFolders(decodedState.expandedFolders || []);
            // Navigate to /pack URL so the address bar reflects the open modal
            if (!location.pathname.endsWith('/pack')) {
              navigate(location.pathname + '/pack', { replace: true });
            } else {
              setBackpackModalOpen(true);
            }
          }
        } catch (error) {
          console.error('Failed to decode stored backpack state:', error);
        }
        
        // Clear the stored state after using it
        sessionStorage.removeItem('backpackState');
      }
    }

    // Open modal when path ends with /pack (e.g., /today/log-habits/pack)
    if (location.pathname.endsWith('/pack')) {
      setBackpackModalOpen(true);
    }
    
    // Close backpack modal when the path no longer ends with /pack
    // (e.g., user pressed browser back button from a */pack URL)
    if (!location.pathname.endsWith('/pack')) {
      setBackpackModalOpen(false);
    }
    
    // Close backpack modal when navigating to editor or viewer page
    // This happens automatically after navigation starts from BackpackModal
    // Keeps modal visible during transition to prevent visual flash
    if (location.pathname.startsWith('/editor/') || location.pathname.startsWith('/viewer/')) {
      setBackpackModalOpen(false);
    }

    // Open RAM modal when path ends with /ram
    if (location.pathname.endsWith('/ram')) {
      setRamModalOpen(true);
    } else {
      setRamModalOpen(false);
    }

    // Open hint modal when path ends with /hint
    if (location.pathname.endsWith('/hint')) {
      setHintModalOpen(true);
    } else {
      setHintModalOpen(false);
    }
  }, [location, navigate]);

  const getPageTitle = () => {
    return '';
  };

  // Hide sidebar and header for editor and viewer pages
  const isEditorPage = location.pathname.startsWith('/editor/');
  const isViewerPage = location.pathname.startsWith('/viewer/');

  if (authState === 'loading') {
    return <AirMaskLoadingScreen />;
  }

  if (location.pathname === '/') {
    if (authState === 'authenticated') {
      return <Navigate to="/today/log-habits" replace />;
    }
    return <WelcomePage />;
  }

  if (location.pathname === '/login') {
    return <LoginPage />;
  }

  if (authState === 'unauthenticated') {
    return <Navigate to="/" replace />;
  }

  // Editor and viewer pages get full screen, no sidebar/header
  if (isEditorPage || isViewerPage) {
    return (
      <Routes>
        <Route path="/editor/:fileId" element={<MarkdownEditorPage />} />
        <Route path="/viewer/:fileId" element={<ImageViewerPage />} />
      </Routes>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <Sidebar
          variant="permanent"
          onOpenHint={() => {
            if (!location.pathname.endsWith('/hint')) {
              const basePath = stripModalSuffix(location.pathname);
              navigate(`${basePath}/hint`);
            }
          }}
          onOpenViewTags={() => setGlobalTagsModalOpen(true)}
        />
      </Box>
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <Sidebar
          variant="temporary"
          open={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
          onOpenHint={() => {
            if (!location.pathname.endsWith('/hint')) {
              const basePath = stripModalSuffix(location.pathname);
              navigate(`${basePath}/hint`);
            }
          }}
          onOpenViewTags={() => setGlobalTagsModalOpen(true)}
        />
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          minHeight: '100vh',
          backgroundColor: 'background.default',
        }}
      >
        <AppBar 
          position="fixed" 
          elevation={0}
          sx={{ 
            width: { xs: '100%', md: `calc(100% - ${DRAWER_WIDTH}px)` }, 
            ml: { xs: 0, md: `${DRAWER_WIDTH}px` },
            zIndex: (theme) => theme.zIndex.drawer + 1,
            borderBottom: 'none',
          }}
        >
          <Toolbar
            data-testid="top-nav-toolbar"
            onMouseEnter={() => setIsHoveringTopRamZone(true)}
            onMouseLeave={() => setIsHoveringTopRamZone(false)}
            sx={{
              userSelect: 'none',
              WebkitUserSelect: 'none',
              WebkitTouchCallout: 'none',
            }}
          >
            <IconButton
              aria-label="open navigation"
              edge="start"
              onClick={() => setMobileNavOpen(true)}
              sx={{
                mr: 1,
                display: { xs: 'inline-flex', md: 'none' },
                color: 'primary.contrastText',
              }}
            >
              <MenuIcon />
            </IconButton>
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              {getPageTitle()}
            </Typography>
            <Box
              data-testid="top-nav-ram-hover-zone"
              sx={{
                flexGrow: 1,
                minHeight: 40,
                mx: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <IconButton
                  data-testid="top-nav-ram-button"
                  aria-label="Open RAM"
                  onClick={openRamRoute}
                  sx={{
                    color: 'primary.contrastText',
                    opacity: 0.9,
                    display: {
                      xs: 'inline-flex',
                      md: isHoveringTopRamZone ? 'inline-flex' : 'none'
                    },
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      opacity: 1,
                    },
                  }}
                >
                  <ArrowDropDownIcon sx={{ fontSize: 30, transform: 'scale(1.5)' }} />
                </IconButton>
              </Box>
            </Box>
            <IconButton 
              onClick={() => {
                if (!location.pathname.endsWith('/pack')) {
                  const basePath = stripModalSuffix(location.pathname);
                  navigate(`${basePath}/pack`);
                }
              }}
              sx={{ 
                ml: 2,
                color: 'primary.contrastText',
              }}
            >
              <BackpackIcon sx={{ fontSize: 28 }} />
            </IconButton>
          </Toolbar>
        </AppBar>
            
        <Box sx={{ mt: 8 }}> {/* Account for AppBar height */}
          <Routes>
            <Route path="/" element={<WelcomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/today/*" element={<TodayPage />} />
            <Route path="/history/*" element={<HistoryPage />} />
            <Route path="/upcoming/*" element={<UpcomingPage />} />
            <Route path="/ram" element={<Navigate to="/today/log-habits/ram" replace />} />
            <Route path="/pack" element={<Navigate to="/today/log-habits/pack" replace />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Box>
        
        {/* Hint Modal */}
        <AirmaskInfoModal
          open={hintModalOpen}
          onClose={() => {
            if (location.pathname.endsWith('/hint')) {
              const basePath = stripModalSuffix(location.pathname);
              navigate(basePath || '/today/log-habits');
            }
            setHintModalOpen(false);
          }}
        />

        <GlobalPasoTagsModal
          open={globalTagsModalOpen}
          onClose={() => setGlobalTagsModalOpen(false)}
        />

        <RAMModal
          open={ramModalOpen}
          onClose={() => {
            if (location.pathname.endsWith('/ram')) {
              const basePath = stripModalSuffix(location.pathname);
              navigate(basePath || '/today/log-habits');
            }
            setRamModalOpen(false);
          }}
        />

        {/* Backpack Modal */}
        <BackpackModal 
          open={backpackModalOpen} 
          onClose={() => {
            // When closing, navigate back to the base path (strip /pack)
            if (location.pathname.endsWith('/pack')) {
              const basePath = location.pathname.slice(0, -5);
              navigate(basePath || '/today/log-habits');
            }
            // Clear sessionStorage to prevent modal from reopening on navigation
            sessionStorage.removeItem('backpackState');
            // Reset the expanded sections state
            // so next time it opens fresh unless navigating back from editor
            setBackpackModalOpen(false);
            setBackpackExpandedSections([]);
            setBackpackNestedExpanded([]);
            setBackpackExpandedFolders([]);
          }}
          initialExpandedSections={backpackExpandedSections}
          initialNestedExpanded={backpackNestedExpanded}
          initialExpandedFolders={backpackExpandedFolders}
        />
      </Box>
    </Box>
  );
}

export { AppContent };

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <RecurringItemsProvider>
        <Router>
          <AppContent />
        </Router>
      </RecurringItemsProvider>
    </ThemeProvider>
  );
}

export default App;
